import { useGSAP } from '@gsap/react';
import { useLayoutEffect, useRef, type RefObject } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router';
import {
  gsap,
  MOTION_QUERY,
  prefersMotion,
  ScrollSmoother,
  scrollToTarget,
  ScrollTrigger,
  SMOOTH_QUERY,
} from '../lib/scroll';
import { CartDrawer } from './cart-drawer';
import { SiteFooter } from './site-footer';
import { SiteHeader } from './site-header';

/**
 * Storefront shell. The header and bag drawer sit outside the smooth-scroll wrapper because
 * ScrollSmoother moves the content with transforms, which would break fixed positioning.
 */
export function StoreLayout() {
  const wrapper = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLElement>(null);

  useSmoothScroll(wrapper, content);
  useReveals(content);
  useRouteScroll(main);

  return (
    <>
      <a
        href="#main"
        className="sr-only z-50 bg-maroon px-4 py-2 text-champagne focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <SiteHeader />
      <div id="smooth-wrapper" ref={wrapper}>
        <div id="smooth-content" ref={content} className="flex min-h-svh flex-col pt-16 sm:pt-20">
          <main id="main" ref={main} className="flex-1">
            <Outlet />
          </main>
          <SiteFooter />
        </div>
      </div>
      <CartDrawer />
    </>
  );
}

/** Inertia scrolling on mouse/trackpad (phones keep native momentum). */
function useSmoothScroll(wrapper: RefObject<HTMLDivElement | null>, content: RefObject<HTMLDivElement | null>) {
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(SMOOTH_QUERY, () => {
      const smoother = ScrollSmoother.create({
        wrapper: wrapper.current!,
        content: content.current!,
        smooth: 1.15,
        effects: true,
        smoothTouch: false,
      });
      return () => smoother.kill();
    });

    // Images, fonts and data arrive after first paint; keep scroll lengths and pins in sync.
    let timer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => ScrollTrigger.refresh(), 120);
    });
    observer.observe(content.current!);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      mm.revert();
    };
  });
}

/**
 * Anything marked data-reveal eases up into view as it enters the viewport; data-reveal="seal"
 * presses in like a sticker. Works for elements that mount later (data loads, filter changes).
 */
function useReveals(content: RefObject<HTMLDivElement | null>) {
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(MOTION_QUERY, (context) => {
      const root = content.current!;
      const registerNew = () => {
        const fresh = [...root.querySelectorAll<HTMLElement>('[data-reveal]:not([data-reveal-ready])')];
        if (fresh.length === 0) return;
        fresh.forEach((el) => el.setAttribute('data-reveal-ready', ''));

        const seals = fresh.filter((el) => el.dataset.reveal === 'seal');
        const rest = fresh.filter((el) => el.dataset.reveal !== 'seal');

        if (rest.length) {
          gsap.set(rest, { autoAlpha: 0, y: 36 });
          ScrollTrigger.batch(rest, {
            start: 'top 92%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.1, ease: 'expo.out', stagger: 0.09, overwrite: true }),
          });
        }
        if (seals.length) {
          gsap.set(seals, { autoAlpha: 0, scale: 1.4, rotate: -16 });
          ScrollTrigger.batch(seals, {
            start: 'top 94%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, { autoAlpha: 1, scale: 1, rotate: 0, duration: 0.9, ease: 'back.out(2)', stagger: 0.1 }),
          });
        }
      };
      // context.add records the tweens/triggers so the matchMedia revert cleans them up.
      const register = () => context.add(registerNew);

      register();
      const observer = new MutationObserver(register);
      observer.observe(root, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
        root.querySelectorAll('[data-reveal-ready]').forEach((el) => el.removeAttribute('data-reveal-ready'));
      };
    });
    return () => mm.revert();
  });
}

/** Waits briefly for a section that renders after its data loads. */
function waitForElement(selector: string, timeout = 2500): Promise<Element | null> {
  return new Promise((resolve) => {
    const started = performance.now();
    const check = () => {
      const el = document.querySelector(selector);
      if (el || performance.now() - started > timeout) resolve(el);
      else requestAnimationFrame(check);
    };
    check();
  });
}

/**
 * New page: start at the top (or where you were, on back/forward), with a soft page-in.
 * Hash link (e.g. /#weaves): glide to that section. Query-only changes (filters) keep position.
 */
function useRouteScroll(main: RefObject<HTMLElement | null>) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const previousPath = useRef<string | null>(null);

  // Remember where each history entry was scrolled to.
  useLayoutEffect(() => {
    const key = location.key;
    return () => {
      positions.current.set(key, ScrollSmoother.get()?.scrollTop() ?? window.scrollY);
    };
  }, [location.key]);

  useLayoutEffect(() => {
    const firstRender = previousPath.current === null;
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;

    if (pathChanged && !firstRender) {
      const saved = navigationType === 'POP' ? positions.current.get(location.key) : undefined;
      scrollToTarget(saved ?? 0, { instant: true });
      if (prefersMotion() && main.current) {
        gsap.fromTo(
          main.current.children,
          { autoAlpha: 0, y: 18 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'expo.out', stagger: 0.06, clearProps: 'opacity,visibility,transform' },
        );
      }
    }

    if (location.hash) {
      let cancelled = false;
      void waitForElement(location.hash).then((el) => {
        if (el && !cancelled) requestAnimationFrame(() => scrollToTarget(el));
      });
      return () => {
        cancelled = true;
      };
    }
  }, [location.key, location.pathname, location.hash, navigationType, main]);
}
